/**
 * AI Recommendations Service
 * Llama a nuestro backend proxy (/api/gcp/ai/summary) que reenvía la petición
 * al GCP gateway con la x-api-key de forma segura (nunca expuesta en el browser).
 */

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;

// Hard timeout — gateway typically replies in 30-60s; abort beyond 120s.
const REQUEST_TIMEOUT_MS = 120_000;

/**
 * Fetch the personalized AI recommendations for a given email.
 * Heavy call: typical latency 30-60s.
 *
 * @param {string} email
 * @param {AbortSignal} [externalSignal] optional external abort signal (e.g. user-cancel)
 * @returns {Promise<object>} Full AI summary payload
 */
export async function fetchAIRecommendations(email, externalSignal) {
  if (!email) {
    throw new Error("Email requerido para generar recomendaciones");
  }

  const url = `${BACKEND_URL}/api/gcp/ai/summary?email=${encodeURIComponent(email)}`;

  console.group("[WE360 GCP] fetchAIRecommendations");
  console.log("Proxy URL:", url);
  console.log("Origin (browser):", window.location.origin);

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort("timeout"),
    REQUEST_TIMEOUT_MS
  );
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort("user-cancel");
    else externalSignal.addEventListener("abort", () => controller.abort("user-cancel"));
  }

  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { accept: "application/json" },
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    console.error("[WE360 GCP] Network error:", err.message);
    console.groupEnd();
    if (err?.name === "AbortError" || controller.signal.aborted) {
      const reason = controller.signal.reason;
      if (reason === "user-cancel") {
        const cancelErr = new Error("Solicitud cancelada");
        cancelErr.name = "AbortError";
        throw cancelErr;
      }
      throw new Error(
        "El servicio IA tardó demasiado en responder (>2 min). Intenta de nuevo."
      );
    }
    throw new Error(
      "No se pudo contactar al servicio IA. Verifica tu conexión e intenta de nuevo."
    );
  }
  clearTimeout(timeoutId);

  console.log("Response status:", res.status, res.ok ? "✅ OK" : "❌ ERROR");
  console.groupEnd();

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    console.error("[WE360 GCP] Error body:", text.slice(0, 200));
    throw new Error(
      `Error ${res.status} del servicio IA${text ? `: ${text.slice(0, 120)}` : ""}`
    );
  }

  const data = await res.json();

  if (data?.status !== "Success") {
    throw new Error(
      data?.message || "El servicio IA no devolvió un resultado válido"
    );
  }

  return data;
}
